import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  AcademicCapIcon,
  EnvelopeIcon,
  KeyIcon,
  MapPinIcon,
  PencilSquareIcon,
  PhoneIcon,
  UserCircleIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { Spinner } from "@material-tailwind/react";
import { portalApi } from "../../services/portalApi";
import { formatMexicoLocation } from "../../utils/mexicoLocations";
import {
  labelAppointmentModality,
  labelConsultationModesSetting,
} from "../../utils/consultationModes";
const WEEKDAY_SHORT = ["", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"];

function formatApptDateTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("es-MX", {
      timeZone: "America/Mexico_City",
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return iso;
  }
}

function DetailField({ icon: Icon, label, children }) {
  return (
    <div className="min-w-0 space-y-1">
      <div className="portal-label flex items-center gap-1.5">
        {Icon ? <Icon className="h-4 w-4 shrink-0 text-brand-gray" aria-hidden /> : null}
        {label}
      </div>
      <div className="portal-value break-words">{children}</div>
    </div>
  );
}

/** Chips por día (opción 2): un chip por día con atención y su(s) rango(s). */
function weeklyChips(weekly) {
  return (weekly || [])
    .filter((block) => (block.ranges || []).length > 0)
    .sort((a, b) => (a.weekday || 0) - (b.weekday || 0))
    .map((block) => ({
      weekday: block.weekday,
      label: WEEKDAY_SHORT[block.weekday] || "?",
      ranges: (block.ranges || [])
        .map((r) => `${String(r.start).slice(0, 5)}–${String(r.end).slice(0, 5)}`)
        .join(" · "),
    }));
}

function ScheduleChips({ weekly }) {
  const chips = weeklyChips(weekly);
  if (!chips.length) {
    return (
      <p className="portal-meta py-4 text-center border border-dashed border-brand-lavender/25 rounded-xl">
        Sin horarios semanales.
      </p>
    );
  }
  return (
    <div className="grid grid-cols-2 gap-2">
      {chips.map((c) => (
        <span
          key={c.weekday}
          className="flex items-center gap-2 rounded-lg border border-brand-lavender/30 bg-brand-lilac/35 px-3 py-2"
        >
          <span className="portal-label !text-brand-navy w-9 shrink-0">
            {c.label}
          </span>
          <span className="portal-value !text-[0.9375rem] tabular-nums">
            {c.ranges}
          </span>
        </span>
      ))}
    </div>
  );
}

/**
 * Drawer lateral con detalle de psicólogo (perfil, especialidad, horario).
 */
export default function AdminPsychDetailDrawer({
  psychologist: initialPsych,
  open,
  onClose,
}) {
  const [psych, setPsych] = useState(initialPsych);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [upcoming, setUpcoming] = useState([]);
  const [loadingAppts, setLoadingAppts] = useState(false);

  useEffect(() => {
    setPsych(initialPsych);
  }, [initialPsych]);

  useEffect(() => {
    if (!open || !initialPsych?.psychologistId) return undefined;

    let cancelled = false;
    setLoadingDetail(true);
    setLoadingAppts(true);
    setUpcoming([]);

    portalApi
      .adminPsychologist(initialPsych.psychologistId)
      .then((r) => {
        if (!cancelled && r?.psychologist) setPsych(r.psychologist);
      })
      .catch(() => {
        /* usamos el row inicial */
      })
      .finally(() => {
        if (!cancelled) setLoadingDetail(false);
      });

    portalApi
      .adminAppointments({
        psychologistId: initialPsych.psychologistId,
        status: "scheduled",
        from: new Date().toISOString(),
        limit: 50,
      })
      .then((r) => {
        if (cancelled) return;
        const next = (r.appointments || [])
          .slice()
          .sort((a, b) => String(a.startUtc).localeCompare(String(b.startUtc)))
          .slice(0, 3);
        setUpcoming(next);
      })
      .catch(() => {
        if (!cancelled) setUpcoming([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingAppts(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, initialPsych?.psychologistId]);

  useEffect(() => {
    if (!open) return undefined;
    function onKey(e) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  if (!open || !psych) return null;

  const name = psych.name || "Psicólogo";
  const locationLabel = formatMexicoLocation(psych.location) || "—";
  const scheduleVersions = psych.schedule?.versions || [];
  const latestWeekly =
    scheduleVersions.length > 0
      ? scheduleVersions[scheduleVersions.length - 1]?.weekly
      : psych.schedule?.weekly;

  return (
    <div
      className="fixed inset-0 z-[100]"
      role="dialog"
      aria-modal="true"
      aria-label={`Detalle de ${name}`}
    >
      <button
        type="button"
        className="absolute inset-0 bg-brand-navy/40 border-0 cursor-default"
        aria-label="Cerrar panel"
        onClick={onClose}
      />
      <aside className="absolute top-0 right-0 bottom-0 flex h-full w-full max-w-md sm:max-w-lg flex-col overflow-hidden bg-brand-navy shadow-2xl animate-[slideInRight_0.2s_ease-out]">
        <div className="shrink-0 bg-brand-navy text-white px-5 pt-5 pb-6">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex items-start gap-3">
              {psych.profileImageUrl ? (
                <img
                  src={psych.profileImageUrl}
                  alt=""
                  className="h-14 w-14 shrink-0 rounded-full object-cover border-2 border-white/20"
                />
              ) : (
                <UserCircleIcon className="h-14 w-14 shrink-0 text-white/50" aria-hidden />
              )}
              <div className="min-w-0">
                <p className="portal-meta !text-white/70 mb-1">Psicólogo</p>
                <h2 className="text-[1.375rem] sm:text-[1.5rem] font-semibold leading-tight break-words">
                  {name}
                </h2>
                <div className="mt-3 flex flex-wrap gap-1.5">
                  <span className="inline-flex rounded-full bg-brand-lavender/90 px-2.5 py-1 text-[0.8125rem] font-semibold">
                    {labelConsultationModesSetting(psych.consultationModes)}
                  </span>
                  <span className="inline-flex rounded-full bg-white/15 px-2.5 py-1 text-[0.8125rem] font-semibold">
                    {psych.hasUserLogin ? "Con acceso portal" : "Sin acceso portal"}
                  </span>
                </div>
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg p-1.5 text-white/80 hover:bg-white/10 hover:text-white transition-colors"
              aria-label="Cerrar"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto bg-white">
          {loadingDetail ? (
            <div className="flex items-center justify-center gap-2 py-10 portal-meta">
              <Spinner className="h-5 w-5" />
              Cargando perfil…
            </div>
          ) : (
            <>
              <div className="px-5 py-5 border-b border-brand-lavender/15">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <DetailField icon={EnvelopeIcon} label="Correo">
                    {psych.email ? (
                      <a
                        href={`mailto:${psych.email}`}
                        className="text-brand-lavender font-normal hover:underline break-all"
                      >
                        {psych.email}
                      </a>
                    ) : (
                      "—"
                    )}
                  </DetailField>
                  <DetailField icon={PhoneIcon} label="Teléfono">
                    {psych.phone ? (
                      <a
                        href={`tel:${String(psych.phone).replace(/\s/g, "")}`}
                        className="hover:underline font-normal"
                      >
                        {psych.phone}
                      </a>
                    ) : (
                      "—"
                    )}
                  </DetailField>
                  <DetailField icon={MapPinIcon} label="Ubicación">
                    {locationLabel}
                  </DetailField>
                  <DetailField icon={MapPinIcon} label="Dirección">
                    {psych.address || "—"}
                  </DetailField>
                  <DetailField icon={KeyIcon} label="Acceso portal">
                    {psych.hasUserLogin ? "Sí" : "No"}
                  </DetailField>
                </div>
              </div>

              <div className="px-5 py-5 border-b border-brand-lavender/15">
                <h3 className="portal-section-title mb-3">Especialidad</h3>
                {psych.specialtyDescription ? (
                  <p className="portal-value whitespace-pre-wrap">
                    {psych.specialtyDescription}
                  </p>
                ) : (
                  <p className="portal-meta py-4 text-center border border-dashed border-brand-lavender/25 rounded-xl">
                    Sin descripción de especialidad.
                  </p>
                )}
              </div>

              <div className="px-5 py-5 border-b border-brand-lavender/15">
                <div className="flex items-baseline justify-between gap-2 mb-3">
                  <h3 className="portal-section-title">Horario</h3>
                  <AcademicCapIcon className="h-5 w-5 text-brand-lavender" aria-hidden />
                </div>
                <ScheduleChips weekly={latestWeekly} />
              </div>

              <div className="px-5 py-5">
                <h3 className="portal-section-title mb-3">Próximas citas</h3>
                {loadingAppts ? (
                  <div className="flex items-center justify-center gap-2 py-6 portal-meta">
                    <Spinner className="h-5 w-5" />
                    Cargando citas…
                  </div>
                ) : upcoming.length === 0 ? (
                  <p className="portal-meta py-4 text-center border border-dashed border-brand-lavender/25 rounded-xl">
                    Sin citas próximas.
                  </p>
                ) : (
                  <ul className="space-y-2.5">
                    {upcoming.map((a) => (
                      <li
                        key={a.appointmentId}
                        className="rounded-xl border border-brand-lavender/20 bg-white p-3.5 shadow-sm"
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <p className="portal-label truncate">
                              {a.clientName || "Cliente"}
                            </p>
                            <p className="portal-meta mt-0.5">
                              {formatApptDateTime(a.startUtc)}
                            </p>
                          </div>
                          {a.modality ? (
                            <span className="shrink-0 inline-flex rounded-full bg-brand-lilac/50 px-2.5 py-1 text-[0.8125rem] font-semibold text-brand-navy">
                              {labelAppointmentModality(a.modality)}
                            </span>
                          ) : null}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </>
          )}
        </div>

        <div className="shrink-0 border-t border-brand-lavender/20 bg-white px-5 py-4">
          <Link
            to={`/admin/psicologos/${psych.psychologistId}/horario`}
            className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-brand-lavender px-4 py-3 text-[1rem] font-semibold text-white shadow-md shadow-brand-lavender/25 hover:brightness-105 transition-all"
          >
            <PencilSquareIcon className="h-5 w-5" aria-hidden />
            Editar perfil y horario
          </Link>
        </div>
      </aside>
      <style>{`
        @keyframes slideInRight {
          from { transform: translateX(100%); }
          to { transform: translateX(0); }
        }
      `}</style>
    </div>
  );
}
